import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import type { JarvisRuntimeState } from "@/hooks/useJarvisRuntime";

interface JarvisOrbProps {
  state: JarvisRuntimeState;
  level: number; // 0..1 (RMS)
  size?: number; // px
  className?: string;
}

const STATE_LABEL: Record<JarvisRuntimeState, string> = {
  idle: "Inativo",
  starting: "Iniciando…",
  listening: "Ouvindo",
  thinking: "Pensando",
  speaking: "Respondendo",
  error: "Erro",
};

/**
 * Orb de presença do Jarvis: círculo central com 3 anéis pulsantes
 * cuja escala depende do nível de áudio (RMS) e cuja cor depende do estado.
 * Renderizado em <canvas> para 60fps suave.
 */
export function JarvisOrb({ state, level, size = 220, className }: JarvisOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const levelRef = useRef(0);
  const stateRef = useRef(state);

  useEffect(() => { levelRef.current = level; }, [level]);
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    let t = 0;
    let smoothLevel = 0;

    const colorFor = (s: JarvisRuntimeState) => {
      // valores HSL extraídos via CSS vars não funcionam no canvas — usamos
      // tokens semânticos do design system traduzidos manualmente.
      switch (s) {
        case "listening": return { h: 142, s: 71, l: 45 }; // primary green
        case "thinking":  return { h: 38,  s: 92, l: 50 }; // amber
        case "speaking":  return { h: 199, s: 89, l: 48 }; // sky blue
        case "error":     return { h: 0,   s: 84, l: 60 }; // red
        case "starting":  return { h: 142, s: 50, l: 45 };
        default:          return { h: 220, s: 10, l: 50 }; // muted
      }
    };

    const draw = () => {
      t += 0.016;
      // suavização exponencial do nível de áudio
      smoothLevel = smoothLevel * 0.82 + levelRef.current * 0.18;
      const cur = stateRef.current;
      const c = colorFor(cur);

      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const baseR = size * 0.18;

      // Anéis externos pulsantes
      const isActive = cur === "listening" || cur === "speaking" || cur === "thinking";
      const audioBoost = Math.min(1, smoothLevel * 12); // ampliar visualmente
      const pulse = isActive ? (0.5 + 0.5 * Math.sin(t * (cur === "thinking" ? 4 : 2.2))) : 0;

      for (let i = 3; i >= 1; i--) {
        const ringR = baseR + i * 22 + pulse * 6 + audioBoost * 14 * i;
        const alpha = (cur === "idle" ? 0.04 : 0.08) + (isActive ? 0.06 / i : 0);
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${c.h}, ${c.s}%, ${c.l}%, ${alpha})`;
        ctx.fill();
      }

      // Glow externo
      const grad = ctx.createRadialGradient(cx, cy, baseR * 0.4, cx, cy, baseR * 2.4);
      grad.addColorStop(0, `hsla(${c.h}, ${c.s}%, ${c.l}%, 0.55)`);
      grad.addColorStop(1, `hsla(${c.h}, ${c.s}%, ${c.l}%, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, baseR * 2.4, 0, Math.PI * 2);
      ctx.fill();

      // Núcleo
      const coreR = baseR * (1 + audioBoost * 0.3 + (isActive ? pulse * 0.06 : 0));
      const coreGrad = ctx.createRadialGradient(cx - coreR * 0.3, cy - coreR * 0.3, coreR * 0.1, cx, cy, coreR);
      coreGrad.addColorStop(0, `hsl(${c.h}, ${Math.min(100, c.s + 10)}%, ${Math.min(70, c.l + 18)}%)`);
      coreGrad.addColorStop(1, `hsl(${c.h}, ${c.s}%, ${c.l}%)`);
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();

      // Highlight
      ctx.beginPath();
      ctx.arc(cx - coreR * 0.35, cy - coreR * 0.4, coreR * 0.18, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fill();

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [size]);

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <canvas
        ref={canvasRef}
        style={{ width: size, height: size }}
        className="rounded-full"
        aria-label={`Jarvis ${STATE_LABEL[state]}`}
      />
      <div className="text-sm font-medium text-muted-foreground tabular-nums">
        {STATE_LABEL[state]}
      </div>
    </div>
  );
}
